import React, { useMemo } from 'react';
import styled from 'styled-components';

import useUser from 'core/hooks/useUser';

import { EXTERNAL_LINKS } from 'core/api/constants';
import { Box, Flex, Link, RouterLink, Logo } from 'core/ui/atoms';
import LogoutButton from 'core/ui/components/LogoutButton';

function Footer(props) {
  const { userCan } = useUser();
  const canLogOut = userCan.logOut();

  return useMemo(() => {
    return (
      <FooterContainer>
        <StyledFooter {...props}>
          <Column>
            <MethodologyLogo style={{ fontSize: '2em' }} />
          </Column>
          <Column>
            <LinkText href={EXTERNAL_LINKS.ourStory} target='_blank'>Our Story</LinkText>
            <LinkText href={EXTERNAL_LINKS.menu} target='_blank'>See Menu</LinkText>
          </Column>
          <Column>
            <LinkText href={EXTERNAL_LINKS.giftCards} target='_blank'>Gift Cards</LinkText>
            <LinkText href={EXTERNAL_LINKS.blog} target='_blank'>Blog</LinkText>
          </Column>
          <Column>
            <LinkText href={EXTERNAL_LINKS.faq} target='_blank'>FAQs</LinkText>
            <LinkText href={EXTERNAL_LINKS.careers} target='_blank'>Careers</LinkText>
          </Column>
          <Column>
            <LinkText href={EXTERNAL_LINKS.contact} target='_blank'>Contact Us</LinkText>
            {
              canLogOut ?
                <LinkButton reload={props.logoutReload || false} btn-unstyled key="nav-sign-out">Log out</LinkButton>
                :
                <RouterLink to='/sign-in'>Log In/Sign Up</RouterLink>
            }
          </Column>
        </StyledFooter>
        <BottomFooter>
          <LinkText href={EXTERNAL_LINKS.privacyPolicy} target='_blank'>Privacy Policy</LinkText>
          <LinkText href={EXTERNAL_LINKS.termsAndConditions} target='_blank'>Terms and Conditions</LinkText>
          <LinkText href={EXTERNAL_LINKS.accessibility} target='_blank'>Accessibility</LinkText>
        </BottomFooter>
      </FooterContainer>
    );
  }, [props, canLogOut]);
}

const FooterContainer = styled(Box)`
  background: ${(p) => p.theme.colors.creamWhite};
`;

const LinkText = styled(Link)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  text-decoration: auto;
`;

const LinkButton = styled(LogoutButton)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const MethodologyLogo = styled(Logo)`
  margin-bottom: 0;
`;

const StyledFooter = styled.footer`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  padding: 2rem 2rem 0;
  background: ${(p) => p.theme.colors.creamWhite};

  a, button {
    display: block;
    margin-bottom: 1.5rem;
    background: transparent;
    line-height: ${(p) => p.theme.typography.lineHeight.base};
    border: none;
    padding: 0;
    text-decoration: none;
  }

  ${(p) => p.theme.max('sm')`
    grid-template-columns: 1fr 1fr;
    gap: 0;
    padding: 2rem 1rem 0;

    a, button {
      margin-bottom: 1rem;
    }
  `}
`;

const Column = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;

  &:first-child {
    ${(p) => p.theme.max('sm')`
      grid-column: 1 / -1;
      margin-bottom: 2rem;
    `}
  }

  ${(p) => p.theme.max('sm')`
    &:not(:first-child) {
      padding: 0;
    }
  `}
`;

const BottomFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
  background: ${(p) => p.theme.colors.creamWhite};
  border-top: 1px solid ${(p) => p.theme.colors.borderLight || '#E5E7EB'};
  padding-left: 20%;

  > a {
    color: ${(p) => p.theme.typography.colors.primary};
    text-decoration: none;
  }

  ${(p) => p.theme.max('sm')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.5rem 0;
    align-items: start;
    padding: 1rem;
    padding-left: 4%;

    > a:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }

    > a:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }

    > a:nth-child(3) {
      grid-column: 1;
      grid-row: 2;
    }
  `}
`;

export default Footer;
